import { Component, OnInit, ViewChildren, QueryList, AfterViewInit, Input} from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router, NavigationStart } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { forkJoin, Subscription } from 'rxjs';
import { tap, filter, finalize, switchMap, map } from "rxjs/operators";
//import { GridOptions, ColDef } from '@ag-grid-enterprise/all-modules';
import { GridOptions, ColDef, GridApi } from 'ag-grid-community';

import { RouterLinkRendererComponent } from '../../../common/components/ag-grid/router-link-renderer.component';
import { ModalInfoComponent } from "../../../common/components/ag-grid/modal-info.component";

import { AuthService } from '../../../services/auth.service';
import { GridFormatterService } from '../../../common/services/grid-formatter.service';
import { DialogService } from '../../../common/services/dialog.service';
import { FlowInstanceService } from '../../../flow/services/flow-instance.service';
import { PermitService } from '../../../common/services/permit.service';

import { FlowIndex } from '../../../common/models/flow.dto';
import { PendingFlow } from "../../../flow/models/flow.model";
import { Permit, PermitType, PermitSearchSpec } from '../../../common/models/permit.model';
import { UserIdentity, LunaClaimValues, LunaClaimTypes } from '../../../models/user-identity.model';
import { WorkOrderStatus } from '../../../common/models/work-order.model';
import { dateComparator } from '../../../common/components/data-table/comparators/data-table.comparators';
import { WellService } from "../../../common/services/well.service";
import { DataViewRequest, GISPoint } from "../../../common/models/well.model";
import { MetadataSearchSpec, ApplicationType, ApplicationState } from "../../../applications/models/applications.models";
import { ApplicationsService } from "../../../applications/services/applications.service";

@Component({
    selector: 'ext-permit-widget',
    templateUrl: './ext-permit-widget.component.html',
    styleUrls: ['./work-order-widget.component.scss'],
    standalone: false
})
export class ExternalPermitWidget implements OnInit, AfterViewInit {
    @Input() widgetTitle: string;

    private _approvedDateField = 'dateIssued';
    private _applicationDueDateField = 'applicationDueDate';
    //This will never have a value until the actual chief's order denial works and sets it.
    private _deniedDateField = "rejectedDate";
    private _createdDateField = 'createdDate';
    private _submittedDateField = 'dateSubmitted';
    private _expiresDateField = 'dateOfExpiration';

    @ViewChildren('grid')
    public grids: QueryList<AgGridAngular>;
    approvedPermitData: Permit[];
    permitWellData: any[];
	approvedGridOptions: GridOptions;
	approvedGridApi?: GridApi;
	notSubmittedGridOptions: GridOptions;
	notSubmittedGridApi?: GridApi;
	pendingGridOptions: GridOptions;
	pendingGridApi?: GridApi;
	returnedGridOptions: GridOptions;
	returnedGridApi?: GridApi;
	permitDeniedGridOptions: GridOptions;
	permitDeniedGridApi?: GridApi;
    statusEnum = WorkOrderStatus;
    typeEnum = PermitType;
    subscriptions: Subscription = new Subscription();
    data: Permit[];
    notSubmittedData: FlowIndex[];
    pendingData: FlowIndex[];
    returnedData: FlowIndex[];
    permitDeniedData: FlowIndex[];
    private hasManagePermitsPermission: boolean = false;

    approvedWellNumberColumn: ColDef = {
        headerClass: "wrapped",
        editable: false,
        headerName: "Well Number",
        headerTooltip: "Well Number",
        valueGetter: this.wellNumberFormatter(),
        tooltipValueGetter: this.wellNumberFormatter(),
        minWidth: 75,
        maxWidth: 200,
        filter: "agTextColumnFilter",
        resizable: true,
    } as ColDef;


    columnApplicationNumber: ColDef = {
        headerClass: "wrapped",
        headerName: 'Application Number',
        headerTooltip: 'Application Number',
        tooltipField: 'flowIdentifier',
        field: 'flowIdentifier',
        cellRendererSelector: (params) => {
            if (params.data.onBehalf) {
                return {
                    component: ModalInfoComponent,
                    params: {
                        modalText: (data) => { return `Application ${data.flowIdentifier} is a paper submission that must be completed by ODNR.`; },
                        text: (data) => { return data.flowIdentifier; }
                    }
                }
            } else if (params.data.targetUrl != null) {
                return {
                    component: RouterLinkRendererComponent,
                    params: {
                        text: params.data.applicationNumber,
                        targetUrl: params.data.targetUrl
                    }
                }
            } else {
                return {
                    component: RouterLinkRendererComponent,
                    params: {
                        textField: 'flowIdentifier',
                        flowIdField: 'flowId'
                    }
                }
            }
        },
        filter: "agTextColumnFilter",
        filterParams: this.formatterService.defaultTextFilterParams,
        minWidth: 125,
        maxWidth: 160
    };

    columnApprovedDate: ColDef = {
        headerClass: "wrapped",
        headerName: 'Approved',
        headerTooltip: 'Approved',
        tooltipValueGetter: this.formatterService.formatDate(this._approvedDateField),
        field: this._approvedDateField,
        valueGetter: this.formatterService.formatDate(this._approvedDateField),
        comparator: dateComparator,
        filter: "agDateColumnFilter",
        filterParams: this.formatterService.defaultDateFilterParams,
        minWidth: 125,
        maxWidth: 125
    };

    columnDeniedDate: ColDef = {
        headerClass: "wrapped",
        headerName: 'Date Denied',
        headerTooltip: "Date Denied",
        tooltipValueGetter: this.formatterService.formatDate(this._deniedDateField),
        field: this._deniedDateField,
        valueGetter: this.formatterService.formatDate(this._deniedDateField),
        comparator: dateComparator,
        filter: "agDateColumnFilter",
        filterParams: this.formatterService.defaultDateFilterParams,
        minWidth: 125,
        maxWidth: 125
    };

    columnCounty: ColDef = {
        headerClass: "wrapped",
        headerName: 'County',
        headerTooltip: 'County',
        tooltipField: 'county',
        field: 'county',
        filter: "agTextColumnFilter",
        filterParams: this.formatterService.defaultTextFilterParams,
        maxWidth: 125,
        minWidth: 125
    };

    columnPermitCounty: ColDef = {
        headerClass: "wrapped",
        headerName: 'County',
        headerTooltip: 'County',
        tooltipValueGetter: this.permitCountyNameFormatter(),
        valueGetter: this.permitCountyNameFormatter(),
        filter: "agTextColumnFilter",
        filterParams: this.formatterService.defaultTextFilterParams,
        maxWidth: 125,
        minWidth: 125
    };

    columnExpiresDate: ColDef = {
        headerClass: "wrapped",
        headerName: 'Expires',
        headerTooltip: 'Expires',
        tooltipValueGetter: this.formatterService.formatDate(this._expiresDateField),
        field: this._expiresDateField,
        valueGetter: this.formatterService.formatDate(this._expiresDateField),
        comparator: dateComparator,
        filter: "agDateColumnFilter",
        filterParams: this.formatterService.defaultDateFilterParams,
        minWidth: 125,
        maxWidth: 125
    };

    columnPermitNumber: ColDef = {
        headerClass: "wrapped",
        headerName: 'API Number',
        headerTooltip: 'API Number',
        tooltipField: 'apiNumber',
        field: 'apiNumber',
        cellRenderer: RouterLinkRendererComponent,
        cellRendererParams: {
            url: '/common/permit',
            idField: 'id',
            textField: 'apiNumber'
        },
        filter: "agTextColumnFilter",
        filterParams: this.formatterService.defaultTextFilterParams,
        minWidth: 125,
        maxWidth: 125
    };

    columnType: ColDef = {
        headerClass: "wrapped",
        headerName: 'Type',
        headerTooltip: 'Type',
        tooltipField: 'name',
        field: 'name',
        maxWidth: 150,
        minWidth: 100
    }

    columnPermitType: ColDef = {
        headerClass: "wrapped",
        headerName: 'Type',
        headerTooltip: 'Type',
        valueGetter: this.typeFormatter(),
        filterValueGetter: this.typeFormatter(),
        tooltipValueGetter: this.typeFormatter(),
        minWidth: 100,
        maxWidth: 150
    };

    columnResponseDue: ColDef = {
        headerClass: "wrapped",
        headerName: 'Due Date',
        headerTooltip: 'Due Date',
        tooltipValueGetter: this.formatterService.formatDate(this._applicationDueDateField),
        field: this._applicationDueDateField,
        valueFormatter: this.formatterService.formatDate(this._applicationDueDateField),
        comparator: dateComparator,
        filter: "agDateColumnFilter",
        filterParams: this.formatterService.defaultDateFilterParams,
        minWidth: 125,
        maxWidth: 125
    };

    columnStatus: ColDef = {
        headerClass: "wrapped",
        headerName: 'Status',
        headerTooltip: 'Status',
        tooltipField: 'state',
        field: 'state',
        minWidth: 100,
        maxWidth: 150,
    };

    columnDateCreated: ColDef = {
        headerClass: "wrapped",
        headerName: 'Created',
        headerTooltip: 'Created',
        tooltipValueGetter: this.formatterService.formatDate(this._createdDateField),
        field: this._createdDateField,
        valueGetter: this.formatterService.formatDate(this._createdDateField),
        comparator: dateComparator,
        filter: "agDateColumnFilter",
        filterParams: this.formatterService.defaultDateFilterParams,
        minWidth: 125,
        maxWidth: 125
    };

    columnDateSubmitted: ColDef = {
        headerClass: "wrapped",
        headerName: 'Submitted',
        headerTooltip: 'Submitted',
        tooltipValueGetter: this.formatterService.formatDate(this._submittedDateField),
        field: this._submittedDateField,
        valueGetter: this.formatterService.formatDate(this._submittedDateField),
        comparator: dateComparator,
        filter: 'agDateColumnFilter',
        filterParams: this.formatterService.defaultDateFilterParams,
        maxWidth: 125,
        minWidth: 125
    }

    columnCompanyName: ColDef = {
        headerClass: "wrapped",
        headerName: 'CompanyName',
        headerTooltip: 'CompanyName',
        tooltipField: 'companyName',
        field: 'companyName',
        filter: "agTextColumnFilter",
        minWidth: 100,
        filterParams: this.formatterService.defaultTextFilterParams,
    };

    columnExpedited: ColDef = {
        headerClass: "wrapped",
        headerName: 'Expedited',
        headerTooltip: 'Expedited',
        tooltipField: 'expedited',
        field: 'expedited',
    };

    columnUrban: ColDef = {
        headerClass: "wrapped",
        headerName: 'Urban',
        headerTooltip: 'Urban',
        tooltipField: 'urban',
        field: 'urban',
    };

    columnPendingTabApplicationNumber: ColDef = {
        headerClass: "wrapped",
        headerName: 'Application Number',
        headerTooltip: 'Application Number',
        tooltipField: 'flowIdentifier',
        field: 'flowIdentifier',
        filter: "agTextColumnFilter",
        filterParams: this.formatterService.defaultTextFilterParams,
        minWidth: 100,
        maxWidth: 160,
    };

    leaseNameColumn: ColDef = {
        headerClass: "wrapped",
        editable: false,
        headerName: "Well Name",
        headerTooltip: "Well Name",
        valueGetter: this.leaseNameFormatter(),
        tooltipValueGetter: this.leaseNameFormatter(),
        minWidth: 75,
        maxWidth: 200,
        filter: "agTextColumnFilter",
        resizable: true
    } as ColDef;

    wellNameColumn: ColDef = {
        headerClass: "wrapped",
        editable: false,
        headerName: "Well Name",
        headerTooltip: "Well Name",
        valueGetter: this.formatterService.formatPotentiallyBlankField("wellName", "N/A"),
        tooltipValueGetter: this.formatterService.formatPotentiallyBlankField("wellName", "N/A"),
        minWidth: 75,
        maxWidth: 200,
        filter: "agTextColumnFilter",
        resizable: true,
    } as ColDef;


    wellNumberColumn: ColDef = {
        headerClass: "wrapped",
        editable: false,
        headerName: "Well Number",
        headerTooltip: "Well Number",
        valueGetter: this.formatterService.formatPotentiallyBlankField("wellNumber", "N/A"),
        tooltipValueGetter: this.formatterService.formatPotentiallyBlankField("wellNumber", "N/A"),
        minWidth: 75,
        maxWidth: 200,
        filter: "agTextColumnFilter",
        resizable: true,
    } as ColDef;

    approvedColumns = [
        this.columnPermitNumber,
        this.columnApprovedDate,
        this.columnExpiresDate,
        this.columnPermitType,
        this.columnPermitCounty,
        this.leaseNameColumn,
        this.approvedWellNumberColumn
    ];

    pendingColumns = [
        this.columnPendingTabApplicationNumber,
        this.columnDateSubmitted,
        this.columnResponseDue,
        this.columnType,
        this.columnCounty,
        this.wellNameColumn,
        this.wellNumberColumn
    ];

    notSubmittedColumns: ColDef[];
    returnedColumns: ColDef[];
    permitDeniedColumns: ColDef[];

    get user(): UserIdentity {
        return this._authService.User;
    }

    constructor(
        private _authService: AuthService,
        private _permitService: PermitService,
        private formatterService: GridFormatterService,
        public dialogService: DialogService,
        private _flowInstanceService: FlowInstanceService,
        public router: Router,
        public datePipe: DatePipe,
        private wellService: WellService,
        private applicationService: ApplicationsService
    ) {
        this.hasManagePermitsPermission = this._authService.User.allClaims.filter(a => a.type == LunaClaimTypes.System && a.value == LunaClaimValues.ManagePermits).length > 0;
        //Check their permissions and switch out application number tab accordingly
        let applicationNumberColumn = this.hasManagePermitsPermission ? this.columnApplicationNumber : this.columnPendingTabApplicationNumber;

        //set grid columns for tabs with potentially clickable application number rows
        this.notSubmittedColumns = [this.columnDateCreated, applicationNumberColumn, this.columnType, this.columnCounty, this.wellNameColumn, this.wellNumberColumn];
        this.returnedColumns = [applicationNumberColumn, this.columnDateSubmitted, this.columnType, this.columnCounty, this.wellNameColumn, this.wellNumberColumn];
        this.permitDeniedColumns = [this.columnDateSubmitted, applicationNumberColumn, this.columnType, this.wellNameColumn, this.wellNumberColumn, this.columnDeniedDate];     
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    ngAfterViewInit() {
    }

    ngOnInit() {
		this.approvedGridOptions = this.formatterService.BuildDefaultGridOptionsWithColumns(this.approvedColumns);
		this.approvedGridOptions.onGridReady = (params) => { this.approvedGridApi = params.api; };
        this.approvedGridOptions.headerHeight = 40;
		this.pendingGridOptions = this.formatterService.BuildDefaultGridOptionsWithColumns(this.pendingColumns);
		this.pendingGridOptions.onGridReady = (params) => { this.pendingGridApi = params.api; };
        this.pendingGridOptions.headerHeight = 40;
		this.notSubmittedGridOptions = this.formatterService.BuildDefaultGridOptionsWithColumns(this.notSubmittedColumns);
		this.notSubmittedGridOptions.onGridReady = (params) => { this.notSubmittedGridApi = params.api; };
        this.notSubmittedGridOptions.headerHeight = 40;
		this.returnedGridOptions = this.formatterService.BuildDefaultGridOptionsWithColumns(this.returnedColumns);
		this.returnedGridOptions.onGridReady = (params) => { this.returnedGridApi = params.api; };
        this.returnedGridOptions.headerHeight = 40;
		this.permitDeniedGridOptions = this.formatterService.BuildDefaultGridOptionsWithColumns(this.permitDeniedColumns);
		this.permitDeniedGridOptions.onGridReady = (params) => { this.permitDeniedGridApi = params.api; };
        this.permitDeniedGridOptions.headerHeight = 40;

        this.subscriptions.add(this._flowInstanceService.getReturnedIncompleteApplication("InProgressPermitApplications", false, this.user.organizationId)
            .pipe(
                tap((data: FlowIndex[]) => {
                    this.notSubmittedData = data;
                }),
                switchMap((data: FlowIndex[]) => {
                    var spec = {
                        applicationTypes: [ApplicationType.Stratigraphic],
                        applicationStates: [ApplicationState.Start, ApplicationState.Input],
                        organizationId: this._authService.User.organizationId
                    } as MetadataSearchSpec;
                    return this.applicationService.searchMetadata(spec);
                }),
                tap((data: PendingFlow[]) => {
                    if (this.notSubmittedData == null) this.notSubmittedData = data;
                    else {
                        data.forEach(d => this.notSubmittedData.push(d));
                    }
                }))
            .subscribe((result: FlowIndex[]) => {
                this.notSubmittedData = this.sortFlowIndices(this.notSubmittedData);
                if (this.notSubmittedGridApi) {
					this.notSubmittedGridApi.setGridOption("rowData", this.notSubmittedData);
                }
            }));

        this.subscriptions.add(this._flowInstanceService.getReturnedIncompleteApplication("InProgressPermitApplications", true, this.user.organizationId).pipe
            (switchMap((data: FlowIndex[]) => {
                var spec = {
                    applicationTypes: [ApplicationType.Stratigraphic],
                    applicationStates: [ApplicationState.Corrections],
                    organizationId: this._authService.User.organizationId
                } as MetadataSearchSpec;
                return this.applicationService.searchMetadata(spec).pipe(
                    map((meta: PendingFlow[]) => {
                        if (data == null) data = [];
                        return data.concat(meta);
                    })
                )
            }))
            .subscribe((result: FlowIndex[]) => {
                this.returnedData = this.sortFlowIndices(result);
            }));

        this.subscriptions.add(this._flowInstanceService.queryFlowIndices("DeniedPermitApplications", this.user.organizationId)
            .subscribe((result: FlowIndex[]) => {
                this.permitDeniedData = this.sortFlowIndices(result);
            }));

        const pendingObs = this._flowInstanceService.queryFlowIndices("PendingPermitApplications", this.user.organizationId);
        
        const pendingDenialObs = this._flowInstanceService.queryFlowIndices("PendingDenialApplications", this.user.organizationId);

        const pendingObs$ = forkJoin(pendingObs, pendingDenialObs).pipe(
            tap(([pendingData, pendingDenialData]) => {
                this.pendingData = pendingData.concat(pendingDenialData);
            }),
            switchMap(() => {
                var spec = {
                    applicationTypes: [ApplicationType.Stratigraphic],
                    applicationStates: [ApplicationState.VerifyCompletion, ApplicationState.InitialReview, ApplicationState.InitialApproval, ApplicationState.ReturnedToInitialApproval, ApplicationState.ReturnedToInitialReview, ApplicationState.FinalApproval],
                    organizationId: this._authService.User.organizationId
                } as MetadataSearchSpec;
                return this.applicationService.searchMetadata(spec);
            }),
            tap((data: FlowIndex[]) => {
                if (this.pendingData == null) this.pendingData = data;
                else {
                    data.forEach(d => this.pendingData.push(d));
                }
            }),
            finalize(() => { this.subscriptions.add(pendingObs$); })).subscribe(
                () => {
                    if (this.pendingGridApi != null) this.pendingGridApi.setGridOption("rowData", this.pendingData);
                },
                (error) => {
                    console.error(error);
                }
            );

        var spec = this.buildSearchSpec();

        const permitSubs$ = this._permitService.findPermits(spec)
            .pipe(
                tap((res: Permit[]) => { this.data = res; }),
                switchMap((res: Permit[]) => {
                    let ids: string[] = [];
                    res.forEach(p => {
                        if (ids.includes(p.apiNumber) == false) ids.push(p.apiNumber);
                    });
                    return this.wellService.getTargetedWellData(ids, DataViewRequest.ExternalApprovedPermitsView).pipe(
                        tap((wells: any[]) => {
                            this.permitWellData = wells;
                        })
                    );
                })
            ).subscribe((d) => {
                this.approvedPermitData = this.data; //handled this way to allow well data a chance to load before data becomes connected to the grid and content is displayed
            });
        this.subscriptions.add(permitSubs$);


        this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
            this.subscriptions.unsubscribe();
        });
    }

    buildSearchSpec(): PermitSearchSpec {
        var spec = new PermitSearchSpec();
        spec.organizationId = this._authService.User.organizationId;
        spec.includeProposedWell = true;
        spec.includeRestorationPlan = false;
        spec.includeCasingPlan = false;
        spec.includeAccessRoutes = false;
        spec.includeContacts = false;
        spec.includeOtherWells = false;
        spec.includeRUMAs = false;
        spec.includeRoyaltyInterests = false;
        spec.includeSectionApprovals = false;
        spec.includeWaterSources = false;
        spec.includeLocationGISPoints = true;
        return spec;
    }

    getDeniedReason() {
        return (obj) => {
            return 'Application Expired';
        }
    }

    onTabClicked(): void {
        setTimeout(() => {
            this.approvedGridApi.autoSizeAllColumns(false);
            this.pendingGridApi.autoSizeAllColumns(false);
            this.notSubmittedGridApi.autoSizeAllColumns(false);
            this.returnedGridApi.autoSizeAllColumns(false);
            this.permitDeniedGridApi.autoSizeAllColumns(false);
        }, 1000);
        
    }

    sortFlowIndices(indices: FlowIndex[]): FlowIndex[] { //sort by date submitted if available, then by created date if not
        return indices.sort((a, b) => {
            if (a.dateSubmitted < b.dateSubmitted) return -1;
            if (a.dateSubmitted > b.dateSubmitted) return 1;
            if (a.createdDate < b.createdDate) return -1;
            if (a.createdDate > b.createdDate) return 1;
            return 0;
        });
    }

    statusFormatter(component) {
        return (obj) => component.statusEnum[obj.data.status] == "NotSubmitted" ? "Not Submitted" : component.statusEnum[obj.data.status];
    }

    typeFormatter() {
        return (obj) => {
            let result = "N/A";
            if (obj.data.type != null) {
                switch (obj.data.type) {
                    case PermitType.BrineHauler: {
                        result = 'Authorization to Transport Brine';
                    }
                    default: {
                        let valueToReturn = obj.data.type as string;
                        let prefix = 'Permit to ';
                        if (!valueToReturn.startsWith(prefix))
                            valueToReturn = `${prefix}${valueToReturn}`;
                        if (obj.data.proposal != null && obj.data.proposal.wellType != null && obj.data.proposal.wellType.type == 'ST_R')
                            valueToReturn = `${valueToReturn} - Stratigraphic Well`;
                        result = valueToReturn;
                    }
                }
            } else if (obj.data.applicationType != null && obj.data.name != null) {
                result = `${obj.data.applicationType} a ${obj.data.name} Well`;
            }
            return result;
        }
    }

    permitCountyNameFormatter() {
        return (obj) => {
            let countyName: string = "N/A";
            let wellHead: GISPoint = (obj.data as Permit).location.gisPoints.find(x => x.pointType == "wellHead");
            if (wellHead != null && wellHead.county != null) {
                countyName = wellHead.county.name;
            }
            return countyName;
        };
    }

    leaseNameFormatter() {
        return (obj) => {
            let wellName: string = "";
            if (obj != null && obj.data != null) {
                let permit = <Permit>obj.data;
                const trimmedName = permit.wellName == null ? "" : permit.wellName.trim();
                if (trimmedName.length > 0) {
                    const trimmedNumber = permit.wellNumber == null ? "" : permit.wellNumber.trim();
                    if (trimmedNumber.length > 0) {
                        wellName = trimmedName.endsWith(trimmedNumber) ? trimmedName.substring(0, trimmedName.length - trimmedNumber.length - 1) : trimmedName;
                    } else {
                        wellName = trimmedName;
                    }
                }
                if (wellName == null || wellName.length === 0) {
                    let relatedWellData = this.permitWellData.find(w => w.id === permit.apiNumber);
                    if (relatedWellData != null) {
                        wellName = relatedWellData.name;
                    }                  
                }                
                            
            }
            if (wellName == null || wellName.length === 0) wellName = "N/A";
            return wellName;
        };
    }

    wellNumberFormatter() {
        return (obj) => {
            let wellNumber: string = "";
            if (obj != null && obj.data != null) {
                let permit = <Permit>obj.data;
                wellNumber = permit.wellNumber;
                if (wellNumber == null || wellNumber.length === 0) {
                    let relatedWellData = this.permitWellData.find(w => w.id === permit.apiNumber);
                    if (relatedWellData != null) {
                        wellNumber = relatedWellData.wellNumber;
                    } 
                }               
            }
            if (wellNumber == null || wellNumber.length === 0) wellNumber = 'N/A';
            return wellNumber;
        }
    }

}
